import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';

import { comparerByNumber } from '@app/comparers';
import { Product } from '@app/models/product';
import {
  ProductActionTypes,
  ProductActionsUnion
} from '@core/actions/product.actions';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AuthActionTypes, AuthActionsUnion } from '../actions/auth.actions';

export interface State extends EntityState<Product> {
  loading: boolean;
  loaded: boolean;
  updating: boolean;
  followsLoaded: number[];
  advancedFilters: any[];
  filteredProductIds: number[];
  filters: any[];
  busy: boolean;
  busyFood: boolean;
  spinnerMessage: string;
  productFood: any[];
  countryBusinesses: any[];
  providersByName: any[];
  originbyName: any[];
  claims: any[];
  brandByName: any[],
  collectionByName: any[],
  typeBrandByName: any[],
  hierarchyN5: any [],
  hierarchyN4: any[],
  hierarchyN3: any[],
  hierarchyN2: any[],
  hierarchyN1: any[],
  countryEnabled: any[],
  productsPurchaseSummary: any,
  productsTroncals: any
}

export const adapter: EntityAdapter<Product> = createEntityAdapter<Product>({
  selectId: (product: Product) => product.id,
  sortComparer: comparerByNumber('ASC', 'code')
});

export const INITIAL_STATE: State = adapter.getInitialState({
  loading: false,
  loaded: false,
  updating: false,
  followsLoaded: [],
  advancedFilters: [],
  filteredProductIds: [],
  filters: [],
  busy: false,
  busyFood: false,
  spinnerMessage: null,
  productFood: [],
  countryBusinesses: [],
  providersByName: [],
  originbyName: [],
  claims: [],
  brandByName: [],
  collectionByName: [],
  typeBrandByName: [],
  hierarchyN5: [],
  hierarchyN4: [],
  hierarchyN3: [],
  hierarchyN2: [],
  hierarchyN1: [],
  countryEnabled: [],
  productsPurchaseSummary: {allProducts: {}, grouped: {}},
  productsTroncals: []
});

export function reducer(
  state: State = INITIAL_STATE,
  action: ProductActionsUnion | AuthActionsUnion
) {
  switch (action.type) {
    case ProductActionTypes.Load:
    case ProductActionTypes.LoadProductsForFoodShowroom:
    case ProductActionTypes.UpdateOneProductsForFoodShowroom:
    // case ProductActionTypes.UpsertProductsLikesForFoodShowroom:
    case ProductActionTypes.SendProductsLikesMailForFoodShowroom:
    case ProductActionTypes.RestoreTrash:
    case ProductActionTypes.EmptyTrash:
    case ProductActionTypes.UploadTechnicalFile:
    case ProductActionTypes.DownloadTechnicalFile:
    case ProductActionTypes.DeleteTechnicalFile:
      return { ...state, loading: true, busy: true, busyFood: true,
        spinnerMessage: 'Cargando...' };

    case ProductActionTypes.SendProductsLikesMailForFoodShowroomProcessPending:
    case ProductActionTypes.MailProductsLikesMailForFoodShowroomProcessPending:
      return { ...state, loading: true, busy: true, busyFood: true,
        spinnerMessage: action.data.message };

    // case ProductActionTypes.SendProductsLikesMailForFoodShowroomSuccess:
    case ProductActionTypes.MailProductsLikesMailForFoodShowroomProcessSuccess:
    case ProductActionTypes.MailProductsLikesMailForFoodShowroomProcessFail:
    case ProductActionTypes.SendProductsLikesMailForFoodShowroomProcessSuccess:
    case ProductActionTypes.SendProductsLikesMailForFoodShowroomProcessFail:
    case ProductActionTypes.SendProductsLikesMailForFoodShowroomFail:
    case ProductActionTypes.UploadTechnicalFileSuccess:
    case ProductActionTypes.UploadTechnicalFileFail:
    case ProductActionTypes.DownloadTechnicalFileSuccess:
    case ProductActionTypes.DownloadTechnicalFileFail:
    case ProductActionTypes.DeleteTechnicalFile:
      return { ...state, loading: false, busy: false, busyFood: false,
        spinnerMessage: null }

    case ProductActionTypes.LoadFail:
      return { ...state, loading: false };

    case ProductActionTypes.LoadSuccess:
      return adapter.addAll(action.products, {
        ...state,
        loading: false,
        loaded: true,
        busy: false,
        spinnerMessage: ''
      });

    case ProductActionTypes.LoadProductsForFoodShowroomSuccess:
      return {
        ...state,
        loading: false,
        loaded: true,
        busy: false,
        busyFood: false,
        spinnerMessage: '',
        productFood: action.productsFood.productsFood,
        countryBusinesses: action.productsFood.countryBusinesses,
        providersByName: action.productsFood.providersByName,
        originbyName: action.productsFood.originbyName,
        brandByName: action.productsFood.brandByName,
        collectionByName: action.productsFood.collectionByName,
        typeBrandByName: action.productsFood.typeBrandByName,
        hierarchyN5: action.productsFood.hierarchyN5,
        hierarchyN4: action.productsFood.hierarchyN4,
        hierarchyN3: action.productsFood.hierarchyN3,
        hierarchyN2: action.productsFood.hierarchyN2,
        hierarchyN1: action.productsFood.hierarchyN1,
        claims: action.productsFood.claims,
        countryEnabled: action.productsFood.countryEnabled,
      }

    case ProductActionTypes.UpdateOneProductsForFoodShowroomSuccess:
      const newState = state.productFood.map(item => {
        if(item.id === action.data.id){
          return action.data
        }
        return item
      })
      return {
        ...state,
        productFood: newState,
        loading: false, 
        busy: false, 
        busyFood: false
      }

    case ProductActionTypes.LoadSingleSuccess:
      return adapter.upsertOne(action.product, state);

    case ProductActionTypes.BulkUpdate:
      return { ...state, updating: true };

    case ProductActionTypes.BulkUpdateFail:
      return { ...state, updating: false };

    case ProductActionTypes.LoadByCountryDepartment:
      return {
        ...state,
        loading: true
      };

    case ProductActionTypes.LoadByCountryDepartmentSuccess:
      return adapter.upsertMany(action.products, {
        ...state,
        loading: false
      });
    case ProductActionTypes.LoadByFollow:
      return {
        ...state,
        loading: true,
        advancedFilters: [...state.advancedFilters, action.opts]
      };
    case ProductActionTypes.LoadByFollowSuccess:
      return adapter.addAll(action.products, {
        ...state,
        loading: false,
        busy: false, 
        followsLoaded: [
          ...state.followsLoaded.filter(id => id !== action.followId),
          action.followId
        ]
      });

    case ProductActionTypes.AddOne:
      return adapter.addOne(action.product, state);

    case ProductActionTypes.AddMany:
      if (Object.values(action.products).length) {
        return adapter.addMany(action.products ? action.products : [], state);
      }
      return {...state};

    case ProductActionTypes.UpdateOne:
      return adapter.updateOne(
        { id: action.product.id, changes: action.product },
        state
      );

    case ProductActionTypes.OptimisticUpdateOne:
      return adapter.updateOne(
        { id: action.ProductId, changes: action.newState },
        state
      );

    case ProductActionTypes.OptimisticUpdateManyFail:
    case ProductActionTypes.OptimisticUpdateManySuccess:
      return adapter.updateMany(
        action.ProductIds.map(id => {
          return { id, changes: action.state };
        }),
        state
      );

    case ProductActionTypes.OptimisticUpdateMany:
      return adapter.updateMany(
        action.ProductIds.map(id => {
          return { id, changes: action.newState };
        }),
        state
      );

    case ProductActionTypes.OptimisticUpdateOneFail:
    case ProductActionTypes.OptimisticUpdateOneSuccess:
      return adapter.updateOne(
        { id: action.ProductId, changes: action.state },
        state
      );

    case ProductActionTypes.AddToFileCount: {
      const id = action.id;
      const item = state.entities[id];
      const prevCount = item ? item.filesCount : null;
      if (prevCount >= 0) {
        return adapter.updateOne(
          { id, changes: { filesCount: prevCount + 1 } },
          state
        );
      } else {
        return state;
      }
    }

    case ProductActionTypes.AddPicture: {
      const id = action.file.ProductId;
      const item = state.entities[id];
      if (item) {
        const pictures = [action.file, ...item.Pictures];
        return adapter.updateOne(
          { id, changes: { Pictures: pictures } },
          state
        );
      } else {
        return state;
      }
    }

    case ProductActionTypes.AddColor: {
      const id = action.ProductId;
      const color = action.color;
      const item = state.entities[id];
      if (item) {
        let colors = item.metadata.colors && item.metadata.colors.length ? item.metadata.colors : []
        item.metadata["colors"] = [...colors, color.name];
        return adapter.updateOne({ id, changes: { metadata: {colors: item.metadata.colors} } }, state);
      } else {
        return state;
      }
    }

    case ProductActionTypes.RemoveColor: {
      const id = action.ProductId;
      const ColorId = action.ColorId;
      const item = state.entities[id];
      if (item) {
        let colors = item.metadata.colors && item.metadata.colors.length ? item.metadata.colors : []
        item.metadata["colors"] = colors.filter(x => x.toLowerCase() !== ColorId.toLowerCase());
        return adapter.updateOne({ id, changes: { metadata: {colors: item.metadata.colors} } }, state);
      } else {
        return state;
      }
    }

    case ProductActionTypes.RemoveFromFileCount: {
      const id = action.id;
      const item = state.entities[id];
      const prevCount = item ? item.filesCount : null;
      if (prevCount > 0) {
        return adapter.updateOne(
          { id, changes: { filesCount: prevCount - 1 } },
          state
        );
      } else {
        return state;
      }
    }

    case ProductActionTypes.EmptyTrashSuccess: {
      const FollowId = action.FollowId;
      let products = Object.keys(state.entities).map(
        key => state.entities[key]
      );

      products = products.filter(
        p => p.FollowId !== FollowId || p.deletedAt === null
      );

      // return adapter.addAll(products, state);
      return adapter.addAll(products, {
        ...state,
        loading: false,
        loaded: true,
        busy: false,
        spinnerMessage: ''
      });
    }

    case ProductActionTypes.DestroySingleSuccess: {
      const ProductId = action.ProductId;

      return adapter.removeOne(ProductId, state);
    }

    case ProductActionTypes.UpsertMany:
      return adapter.upsertMany(action.products, state);

    case AuthActionTypes.Logout:
      return INITIAL_STATE;

    case ProductActionTypes.Search:
      return { ...state, loading: true, filteredProductIds: [] };

    case ProductActionTypes.SearchSuccess:
      return {
        ...state,
        filteredProductIds:
          action.ProductIds.length === 0 ? [-1] : action.ProductIds,
        loading: false
      };

    case ProductActionTypes.SearchFail:
      return { ...state, loading: false, filteredProductIds: [] };

    case ProductActionTypes.SearchClear:
      return { ...state, filteredProductIds: [] };

    case ProductActionTypes.BulkDestroy:
      return { ...state, updating: true, loading: true };
    
    case ProductActionTypes.ProductsPurchaseSummary:
      return {...state, loading: true}

    case ProductActionTypes.ProductsPurchaseSummarySuccess:
      return {...state, loading: false, productsPurchaseSummary: action.data}

    case ProductActionTypes.BulkDestroySuccess:
      return adapter.updateMany(
        action.ProductIds.map(id => {
          return { id, changes: { deletedAt: action.data.deletedAt } };
        }),
        {
          ...state,
          updating: false,
          loading: false
        }
      );

    case ProductActionTypes.BulkDestroyFail:
      return { ...state, updating: false, loading: false };

    case ProductActionTypes.ProductsTroncalsByCountriesSuccess:
      return {
        ...state,
        productsTroncals: action.data
      }

    case ProductActionTypes.Filter:
      return {
        ...state,
        filters: [
          ...state.filters.filter(
            (id => id.valueCondition !== action.key.valueCondition) &&
              (key => key.condition !== action.key.condition)
          ),
          action.key
        ]
      };

    case ProductActionTypes.FilterClear:
      return {
        ...state,
        filters: state.filters.filter(
          item => item.valueCondition !== action.key.valueCondition
        )
      };

    case ProductActionTypes.Clear:
      return INITIAL_STATE;

    default:
      return state;
  }
}

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal
} = adapter.getSelectors();

export const selectLoaded = (state: State) => state.loaded;
export const selectLoading = (state: State) => state.loading;
export const selectFollowsLoaded = (state: State) => state.followsLoaded;
export const selectFilteredProductIds = (state: State) =>
  state.filteredProductIds;

export const selectAdvancedFilters = (state: State) => state.advancedFilters;
export const selectFilters = (state: State) => state.filters;

// export const getState = createFeatureSelector<State>('filters');
export const getState = createFeatureSelector<State>('products');
export const getAdvancedFilters = createSelector(
  getState,
  selectAdvancedFilters
);

export const getFilteredProductIds = createSelector(
  getState,
  selectFilteredProductIds
);

export const getFilters = createSelector(
  getState,
  selectFilters
);

export const getBusy = createSelector(
  getState,
  (state: State) => state.busy
);

export const getSpinnerMessage = createSelector(
  getState,
  (state: State) => state.spinnerMessage
);

export const getProductsFood = createSelector(
  getState,
  (state: State) => state.productFood
);

export const getBusyProductsFood = createSelector(
  getState,
  (state: State) => state.busyFood
)

export const getCountryBusinessUnit = createSelector(
  getState,
  (state: State) => state.countryBusinesses
)

export const getProviderByName = createSelector(
  getState,
  (state: State) => state.providersByName
)

export const getOriginByName = createSelector(
  getState,
  (state: State) => state.originbyName
)

export const getClaims = createSelector(
  getState,
  (state: State) => state.claims
)

export const getCollectionByName = createSelector(
  getState,
  (state: State) => state.collectionByName
)

export const getBrandByName = createSelector(
  getState,
  (state: State) => state.brandByName
)

export const getTypeBrandByName = createSelector(
  getState,
  (state: State) => state.typeBrandByName
)

export const getHierachyN5 = createSelector(
  getState,
  (state: State) => state.hierarchyN5
)

export const getHierachyN4 = createSelector(
  getState,
  (state: State) => state.hierarchyN4
)

export const getHierachyN3 = createSelector(
  getState,
  (state: State) => state.hierarchyN3
)

export const getHierachyN2 = createSelector(
  getState,
  (state: State) => state.hierarchyN2
)

export const getHierachyN1 = createSelector(
  getState,
  (state: State) => state.hierarchyN1
)

export const getCountryEnabled = createSelector(
  getState,
  (state: State) => state.countryEnabled
)

export const getProductsPurchaseSummary = createSelector(
  getState,
  (state: State) => state.productsPurchaseSummary
)

export const getProductsTroncals = createSelector(
  getState,
  (state: State) => state.productsTroncals
)